/**
 * Extract URLs Tool
 *
 * Extract all URLs from text using regex.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

// Matches http/https/ftp URLs with practical boundary handling
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/g;

export const ExtractUrlsTool: ITransformerTool = {
  id: 'extract-urls',
  name: 'Extract URLs',
  description: 'Extract all URLs from text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['url', 'extract', 'find', 'parse', 'link', 'regex'],

  seo: {
    slug: 'url-extractor',
    pageTitle: 'URL Extractor - Free Online Tool | SWE DevTools',
    metaDescription: 'Extract all URLs from any text. Outputs a clean, deduplicated list of links. Fast, private, runs entirely in your browser.',
    keywords: ['url extractor', 'extract urls online', 'find urls in text', 'link extractor', 'url parser'],
    relatedTools: ['extract-emails', 'url-parser', 'find-replace'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'deduplicate',
      type: 'boolean',
      label: 'Remove Duplicates',
      defaultValue: true,
      description: 'Remove duplicate URLs from the output',
    },
    {
      name: 'sort',
      type: 'boolean',
      label: 'Sort',
      defaultValue: false,
      description: 'Sort URLs alphabetically',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '');
    if (!text.trim()) return '';

    const deduplicate = config.deduplicate !== false;
    const sort = config.sort === true;

    // Clean trailing punctuation that's likely not part of the URL
    let urls = Array.from(text.matchAll(URL_REGEX))
      .map((m) => m[0].replace(/[),.:;!?]+$/, ''));

    if (deduplicate) {
      urls = [...new Set(urls)];
    }

    if (sort) {
      urls.sort((a, b) => a.localeCompare(b));
    }

    if (urls.length === 0) {
      return '(no URLs found)';
    }

    return urls.join('\n');
  },
};
